import { useState, useEffect } from 'react';
import './VideoSection.css';
import { useTranslation } from '../i18n/LanguageContext.jsx';
import { API_URL } from '../config/api.js';
import { sanitizeYouTubeId } from '../lib/youtube/sanitize.js';

// API request timeout in milliseconds
const API_TIMEOUT = 15000;
const MAX_RETRIES = 1;

function VideoSection() {
  const { t, language } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVideos, setActiveVideos] = useState({});

  useEffect(() => {
    // Track if effect is still active for cleanup
    let isActive = true;
    let controller = null;
    let timeoutId = null;

    async function fetchVideos() {
      setLoading(true);
      setError(null);

      try {
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
          controller = new AbortController();
          timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

          try {
            const response = await fetch(`${API_URL}/api/videos?lang=${language}`, {
              signal: controller.signal,
            });

            if (!response.ok) {
              throw new Error(`API error: ${response.status}`);
            }

            const data = await response.json();

            if (!isActive) {
              return;
            }

            if (data.videos && data.videos.length > 0) {
              setVideos(data.videos);
            } else {
              // Backend returned empty - treat as unavailable
              setVideos([]);
              setError('empty');
            }

            return;
          } catch (err) {
            if (!isActive) {
              return;
            }

            const isTimeout = err?.name === 'AbortError';
            const isNetworkError = err instanceof TypeError;
            const isServerError =
              typeof err?.message === 'string' && /API error: 5\d{2}/.test(err.message);
            const isRetryableError = isTimeout || isNetworkError || isServerError;
            const shouldRetry = attempt < MAX_RETRIES && isRetryableError;

            if (shouldRetry) {
              console.warn(`Video fetch attempt ${attempt + 1} failed, retrying...`, err);
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              controller = null;
              continue;
            }

            if (isTimeout) {
              console.warn('API request timed out');
              setError('timeout');
            } else {
              console.warn('Failed to fetch videos from API:', err);
              setError('api-error');
            }

            // No fallback - the backend is the single source of truth
            setVideos([]);
            return;
          } finally {
            if (timeoutId) clearTimeout(timeoutId);
          }
        }
      } finally {
        if (isActive) setLoading(false);
      }
    }

    fetchVideos();

    // Cleanup on unmount or language change - only abort in-flight requests
    return () => {
      isActive = false;
      if (controller) controller.abort();
    };
  }, [language]); // Re-fetch when language changes

  // Get video title - use API title or fall back to i18n, with safe default
  const getVideoTitle = (video, index) => {
    if (video.title) {
      return video.title;
    }
    // Fallback to i18n translation with safe default
    const i18nKey = `videos.video${index + 1}Title`;
    const translated = t(i18nKey);
    // If translation key not found, return a generic title
    return translated !== i18nKey ? translated : 'explainIT Video';
  };

  return (
    <section className="video-section">
      <div className="container">
        <h2 className="section-title">{t('videos.sectionTitle')}</h2>

        {loading && (
          <div className="video-loading">
            <div className="loading-spinner"></div>
            <p>{t('videos.loading') || 'Loading videos...'}</p>
          </div>
        )}

        {!loading && (
          <div className="video-grid">
            {videos.map((video, index) => {
              // Sanitize video ID to prevent XSS attacks
              const safeVideoId = sanitizeYouTubeId(video.youtube_id || video.id);
              // Skip rendering if video ID is invalid
              if (!safeVideoId) {
                console.warn('Skipping video with invalid ID at index:', index);
                return null;
              }
              const isActive = activeVideos[safeVideoId];
              return (
                <div key={safeVideoId} className="video-card">
                  <div className="video-wrapper">
                    {isActive ? (
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${safeVideoId}?autoplay=1`}
                        title={getVideoTitle(video, index)}
                        sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                      ></iframe>
                    ) : (
                      <button
                        className="video-facade"
                        onClick={() =>
                          setActiveVideos((prev) => ({ ...prev, [safeVideoId]: true }))
                        }
                        aria-label={`${t('videos.play')}: ${getVideoTitle(video, index)}`}
                        type="button"
                      >
                        <picture>
                          <source
                            srcSet={`https://i.ytimg.com/vi_webp/${safeVideoId}/hqdefault.webp`}
                            type="image/webp"
                          />
                          <img
                            src={`https://i.ytimg.com/vi/${safeVideoId}/hqdefault.jpg`}
                            alt={getVideoTitle(video, index)}
                            className="video-thumbnail"
                            loading={index === 0 ? 'eager' : 'lazy'}
                            fetchPriority={index === 0 ? 'high' : 'auto'}
                            width="480"
                            height="360"
                          />
                        </picture>
                        <span className="video-play-button" aria-hidden="true">
                          <svg viewBox="0 0 68 48" width="68" height="48">
                            <path
                              className="video-play-bg"
                              d="M66.52,7.74c-0.78-2.93-2.49-5.41-5.42-6.19C55.79,.13,34,0,34,0S12.21,.13,6.9,1.55 C3.97,2.33,2.27,4.81,1.48,7.74C0.06,13.05,0,24,0,24s0.06,10.95,1.48,16.26c0.78,2.93,2.49,5.41,5.42,6.19 C12.21,47.87,34,48,34,48s21.79-0.13,27.1-1.55c2.93-0.78,4.64-3.26,5.42-6.19C67.94,34.95,68,24,68,24S67.94,13.05,66.52,7.74z"
                              fill="#212121"
                              fillOpacity="0.8"
                            />
                            <path d="M 45,24 27,14 27,34" fill="#fff" />
                          </svg>
                        </span>
                      </button>
                    )}
                  </div>
                  <h3 className="video-title">{getVideoTitle(video, index)}</h3>
                </div>
              );
            })}
          </div>
        )}

        {error && !loading && videos.length === 0 && (
          <p className="video-error-note">{t('videos.unavailable')}</p>
        )}
      </div>
    </section>
  );
}

export default VideoSection;
